import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Truck, PackageCheck, AlertTriangle, Loader2, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";

function VendorModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", contact_name: "", email: "", phone: "" });
  const save = async () => {
    try { await api.post("/vendors", f); toast.success("Vendor added"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm" data-testid="vendor-modal">
      <div className="relative w-full max-w-md bg-[#F9F7F3] rounded-3xl shadow-2xl p-8">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B]"><X size={20} /></button>
        <h3 className="font-serif text-2xl text-[#1A242B] mb-4">New vendor</h3>
        <div className="space-y-3">
          <input placeholder="Vendor name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="vendor-name" className="eyw-input w-full" />
          <input placeholder="Contact name" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} className="eyw-input w-full" />
          <input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="eyw-input w-full" />
          <input placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="eyw-input w-full" />
        </div>
        <button onClick={save} data-testid="vendor-save" className="eyw-btn w-full mt-5">Save vendor</button>
      </div>
    </div>
  );
}

function POModal({ vendors, products, onClose, onSaved }) {
  const [vendorId, setVendorId] = useState("");
  const [lines, setLines] = useState([]);
  const add = (p) => setLines((l) => l.find((x) => x.product_id === p.id) ? l : [...l, { product_id: p.id, title: p.title, qty: 1, unit_cost: p.cost || 0 }]);
  const total = lines.reduce((s, l) => s + l.qty * l.unit_cost, 0);
  const save = async () => {
    if (lines.length === 0) { toast.error("Add at least one product"); return; }
    const v = vendors.find((x) => x.id === vendorId);
    try {
      await api.post("/purchase-orders", { vendor_id: vendorId, vendor_name: v?.name || "", items: lines });
      toast.success("Purchase order created"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm py-10 overflow-y-auto" data-testid="po-modal">
      <div className="relative w-full max-w-lg bg-[#F9F7F3] rounded-3xl shadow-2xl p-8">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B]"><X size={20} /></button>
        <h3 className="font-serif text-2xl text-[#1A242B] mb-4">New purchase order</h3>
        <select value={vendorId} onChange={(e) => setVendorId(e.target.value)} data-testid="po-vendor" className="eyw-input w-full mb-4">
          <option value="">Select vendor…</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <p className="text-xs text-[#4A5D6B] mb-2">Add products:</p>
        <div className="flex flex-wrap gap-2 mb-4 max-h-32 overflow-y-auto">
          {products.filter((p) => p.type === "physical").map((p) => (
            <button key={p.id} onClick={() => add(p)} data-testid={`po-add-${p.id}`} className="text-xs rounded-full border border-[#E5E0D8] px-3 py-1.5 hover:border-[#144F75]">{p.title}</button>
          ))}
        </div>
        {lines.map((l, i) => (
          <div key={l.product_id} className="flex items-center gap-2 mb-2 text-sm">
            <span className="flex-1 truncate">{l.title}</span>
            <input type="number" value={l.qty} onChange={(e) => setLines((ls) => ls.map((x, idx) => idx === i ? { ...x, qty: Number(e.target.value) } : x))} className="w-16 eyw-input !py-1" data-testid={`po-qty-${i}`} />
            <input type="number" step="0.01" value={l.unit_cost} onChange={(e) => setLines((ls) => ls.map((x, idx) => idx === i ? { ...x, unit_cost: Number(e.target.value) } : x))} className="w-20 eyw-input !py-1" placeholder="cost" />
          </div>
        ))}
        <div className="flex justify-between font-serif text-lg text-[#1A242B] mt-3"><span>Total cost</span><span>{money(total)}</span></div>
        <button onClick={save} data-testid="po-save" className="eyw-btn w-full mt-4">Create PO</button>
      </div>
    </div>
  );
}

export default function POSInventory() {
  const qc = useQueryClient();
  const [vendorModal, setVendorModal] = useState(false);
  const [poModal, setPoModal] = useState(false);
  const vendors = useQuery({ queryKey: ["vendors"], queryFn: () => api.get("/vendors").then((r) => r.data) });
  const pos = useQuery({ queryKey: ["purchase-orders"], queryFn: () => api.get("/purchase-orders").then((r) => r.data) });
  const products = useQuery({ queryKey: ["inv-products"], queryFn: () => api.get("/products").then((r) => r.data) });

  const receive = async (id) => { await api.put(`/purchase-orders/${id}/status`, { status: "received" }); qc.invalidateQueries({ queryKey: ["purchase-orders"] }); qc.invalidateQueries({ queryKey: ["inv-products"] }); toast.success("Received — stock updated"); };
  const adjust = async (p, delta) => { await api.post("/pos/inventory/adjust", { product_id: p.id, delta, reason: "manual adjust" }); qc.invalidateQueries({ queryKey: ["inv-products"] }); };

  const tracked = (products.data || []).filter((p) => p.track_stock);
  const low = tracked.filter((p) => p.stock <= 5);

  return (
    <div data-testid="pos-inventory" className="space-y-8">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="eyw-card p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><Truck size={18} /> Vendors</h3>
            <button onClick={() => setVendorModal(true)} data-testid="inv-add-vendor" className="eyw-btn-ghost !py-1.5 text-xs"><Plus size={14} /> Vendor</button>
          </div>
          <ul className="space-y-2">
            {(vendors.data || []).map((v) => <li key={v.id} className="text-sm flex justify-between border-b border-[#E5E0D8] pb-2 last:border-0"><span className="text-[#1A242B]">{v.name}</span><span className="text-xs text-[#4A5D6B]">{v.email}</span></li>)}
            {vendors.data?.length === 0 && <p className="text-sm text-[#4A5D6B]">No vendors yet.</p>}
          </ul>
        </div>
        <div className="eyw-card p-6">
          <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-4"><AlertTriangle size={18} className="text-[#B5794E]" /> Low stock</h3>
          <ul className="space-y-2">
            {low.map((p) => (
              <li key={p.id} className="text-sm flex items-center justify-between" data-testid={`inv-low-${p.id}`}>
                <span className="text-[#1A242B] truncate flex-1">{p.title}</span>
                <span className="text-[#B5794E] w-10 text-right">{p.stock}</span>
                <button onClick={() => adjust(p, 1)} className="ml-2 h-6 w-6 rounded-full border border-[#E5E0D8] text-[#144F75]" data-testid={`inv-inc-${p.id}`}>+</button>
                <button onClick={() => adjust(p, -1)} className="ml-1 h-6 w-6 rounded-full border border-[#E5E0D8] text-[#B5794E]" data-testid={`inv-dec-${p.id}`}>−</button>
              </li>
            ))}
            {low.length === 0 && <p className="text-sm text-[#4A5D6B]">All stock levels healthy.</p>}
          </ul>
        </div>
      </div>

      <div className="eyw-card p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><PackageCheck size={18} /> Purchase orders</h3>
          <button onClick={() => setPoModal(true)} data-testid="inv-add-po" className="eyw-btn !py-2 text-sm"><Plus size={16} /> New PO</button>
        </div>
        <table className="w-full text-sm hidden md:table">
          <thead className="text-[#4A5D6B]"><tr><th className="text-left py-2">Vendor</th><th className="text-left">Items</th><th className="text-left">Cost</th><th className="text-left">Status</th><th></th></tr></thead>
          <tbody>
            {(pos.data || []).map((po) => (
              <tr key={po.id} className="border-t border-[#E5E0D8]" data-testid={`po-row-${po.id}`}>
                <td className="py-2 text-[#1A242B]">{po.vendor_name || "—"}</td>
                <td>{po.items?.length || 0}</td>
                <td className="text-[#144F75]">{money(Number(po.total_cost || 0))}</td>
                <td><span className="text-xs px-2 py-0.5 rounded-full bg-[#144F75]/10 text-[#144F75]">{po.status}</span></td>
                <td className="text-right">{po.status !== "received" && <button onClick={() => receive(po.id)} data-testid={`po-receive-${po.id}`} className="eyw-btn-ghost !py-1 !px-3 text-xs">Receive</button>}</td>
              </tr>
            ))}
            {pos.data?.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-[#4A5D6B]">No purchase orders yet.</td></tr>}
          </tbody>
        </table>
        <div className="md:hidden space-y-3">
          {(pos.data || []).map((po) => (
            <div key={po.id} data-testid={`po-card-${po.id}`} className="border border-[#E5E0D8] rounded-xl p-3">
              <div className="flex items-start justify-between gap-3">
                <p className="text-[#1A242B] font-medium">{po.vendor_name || "—"}</p>
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#144F75]/10 text-[#144F75] shrink-0">{po.status}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-[#4A5D6B]">{po.items?.length || 0} item(s)</span>
                <span className="text-[#144F75] font-semibold">{money(Number(po.total_cost || 0))}</span>
              </div>
              {po.status !== "received" && <button onClick={() => receive(po.id)} data-testid={`po-receive-${po.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs mt-3">Receive</button>}
            </div>
          ))}
          {pos.data?.length === 0 && <p className="py-8 text-center text-[#4A5D6B] text-sm">No purchase orders yet.</p>}
        </div>
      </div>

      {vendorModal && <VendorModal onClose={() => setVendorModal(false)} onSaved={() => qc.invalidateQueries({ queryKey: ["vendors"] })} />}
      {poModal && <POModal vendors={vendors.data || []} products={products.data || []} onClose={() => setPoModal(false)} onSaved={() => qc.invalidateQueries({ queryKey: ["purchase-orders"] })} />}
    </div>
  );
}
